import React from "react";
import { classNames } from "@features/utils/reactFunctions";
import Coupon20Svg from "./coupon20Icon.svg";
import Coupon50Svg from "./coupon50Icon.svg";

import { Input, notification } from "antd";
import Tooltip from "@funds/pages/MyFunds/parts/Tooltip";
import RecapKeyService from "@services/RecapKey.service";
import { DEFAULT_TIMELOCK_STAKING_MIN_VALUE } from "@bundles/consts";
import CopyIcon from "../../icons/copy-icon-small.svg";

import * as styles from "./CouponStyle.module.scss";
import { Button } from "../Button";

export type RecapCouponState =
  | "disabled"
  | "active"
  | "revealed"
  | "disabled_by_tier";

type Props = {
  discountAmount: string;
  state: RecapCouponState;
  couponCode: string | null;
  setDiscountCodes: React.Dispatch<React.SetStateAction<any>>;
  investorId: number;
  timelockStakeMinValue?: number;
};

const Coupon = ({
  discountAmount,
  state,
  couponCode,
  setDiscountCodes,
  investorId,
  timelockStakeMinValue,
}: Props) => {
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(couponCode ?? "");
      notification.success({
        message: "Coupon code copied to clipboard",
      });
    } catch (err) {
      notification.error({
        message: "Failed to copy coupon code to clipboard",
      });
    }
  };

  const generateRecapKey = (type: any) => {
    RecapKeyService.generateDiscountCoupon(investorId, type).then((resp) => {
      setDiscountCodes((prev: any) => ({
        ...prev,
        [type]: {
          state: "revealed",
          couponCode: resp.data.recap_coupon_code,
        },
      }));

      notification.success({
        message: "Coupon code generated",
      });
    });
  };

  const renderActions = () => {
    switch (state) {
      case "disabled_by_tier":
        return (
          <div className="tailwind-antd-input flex items-center bg-white">
            <p className="mb-0">Tier 1 requires unlocking Recap Coupon</p>
          </div>
        );

      case "disabled":
        return (
          <div className="tailwind-antd-input justify-between flex items-center bg-white">
            <p className="mb-0 text-grey-lightest">
              Unlock now with our Staking Solution
            </p>
            <Tooltip
              title=""
              showArrow={false}
              placement="top"
              tooltipMessage={`You need to stake ${new Intl.NumberFormat(
                "en-US",
                {
                  style: "currency",
                  currency: "USD",
                  currencyDisplay: "narrowSymbol",
                  minimumFractionDigits: 0,
                }
              ).format(
                timelockStakeMinValue ?? DEFAULT_TIMELOCK_STAKING_MIN_VALUE
              )} worth of ETH with at least 1-year timelock`}
            />
          </div>
        );

      case "revealed":
        if (!couponCode) return null;

        return (
          <div className="flex items-center gap-2">
            <Input
              className="tailwind-antd-input"
              value={couponCode}
              readOnly
            />

            <Button
              className="h-[54px] flex items-cener gap-2"
              isPrimary
              onClick={copyToClipboard}
            >
              Copy
              <CopyIcon />
            </Button>
          </div>
        );

      default:
        return (
          <Button
            className="w-full"
            isPrimary
            onClick={() => generateRecapKey(Number(discountAmount))}
          >
            Generate a coupon
          </Button>
        );
    }
  };

  return (
    <div className={classNames(styles.coupon, styles[`coupon-${state}`])}>
      <div className="flex items-center justify-between mb-[30px]">
        <div>
          <p className="font-semibold mb-0">Discount coupon</p>
          <p className="text-sm mb-0">{discountAmount}% for first year</p>
        </div>

        {discountAmount === "20" && <Coupon20Svg />}
        {discountAmount === "50" && <Coupon50Svg />}
      </div>
      <div>{renderActions()}</div>
    </div>
  );
};

export default Coupon;
